import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input, Radio, Tooltip } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { CommissionsBatchesTreeSelect } from "../../../../enumerations/components/form/CommissionsBatchesTreeSelect";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { DEFAULT_SPLIT_COMMISSIONS_QUERY_PARAMS } from "../../api";
import { SplitCommissionsReport } from "../../enums";
import { SplitCommissionsFilterPageRequest } from "../../types";

const INPUTS_WIDHT = 260;

type Props = {
  searchParams: SplitCommissionsFilterPageRequest;
};

export const SplitCommissionsFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<SplitCommissionsFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      report: searchParams.report ?? SplitCommissionsReport.PLANNED_IN_FINISHED_BATCH,
    });
  }, [searchParams]);

  const handleFilterSubmit = (filter: SplitCommissionsFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_SPLIT_COMMISSIONS_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    const report = form.getFieldValue("report");

    form.resetFields();
    form.setFieldsValue({ report });
    handleFilterSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.splitCommissions.titles.main")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="splitCommissionsFilterForm">
        <Flex justify="center">
          <Form.Item name="report" noStyle>
            <Radio.Group
              size="large"
              optionType="button"
              buttonStyle="solid"
              options={Object.values(SplitCommissionsReport).map((report) => ({
                label: (
                  <Tooltip title={t("commissions.enums.splitCommissionsReport." + report + "_tooltip")} key={report}>
                    {t(`commissions.enums.splitCommissionsReport.${report}`)}
                  </Tooltip>
                ),
                value: report,
              }))}
              onChange={(event) => {
                form.setFieldsValue({ report: event.target.value });
                handleFilterSubmit(form.getFieldsValue());
              }}
            />
          </Form.Item>
        </Flex>

        <Flex justify="center" align="end" gap={rowGutter} className="margin-top-medium">
          <Form.Item
            style={{ width: INPUTS_WIDHT }}
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.splitCommissions.helpers.searchHint")} />
          </Form.Item>

          <Form.Item name="gainerIds" style={{ width: INPUTS_WIDHT }} label={t("contract.filter.gainerIds")}>
            <AgentSelect allowClear isMultiple />
          </Form.Item>

          <InstitutionSelect
            formItemProps={{
              style: { width: INPUTS_WIDHT },
              name: "institutionIds",
              label: t("common.institution"),
              rules: [validations.none],
            }}
            selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
            optionsProps={{ groupByType: true }}
          />

          <Form.Item
            name="originalBatchIds"
            style={{ width: INPUTS_WIDHT }}
            label={t("commissions.splitCommissions.filter.originalBatchIds")}
          >
            <CommissionsBatchesTreeSelect />
          </Form.Item>

          <Form.Item
            name="payoutBatchIds"
            style={{ width: INPUTS_WIDHT }}
            label={t("commissions.splitCommissions.filter.payoutBatchIds")}
          >
            <CommissionsBatchesTreeSelect />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
              {t("common.filter")}
            </Button>
          </Form.Item>
        </Flex>
      </Form>
    </Card>
  );
};
